import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, KeyRound, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY_NEW = { email: "", password: "", name: "", role: "doctor", doctor_name: "", license_number: "", specialty: "", ptr_number: "" };
const EMPTY_ASSIGN = { email: "", role: "doctor" };

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_NEW);
  const [assignDraft, setAssignDraft] = useState(EMPTY_ASSIGN);
  const [editing, setEditing] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [pwTarget, setPwTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setItems(data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Admin only");
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.email || !draft.password || !draft.name) {
      toast.error("Email, password, and name are required");
      return;
    }
    try {
      await api.post("/users", draft);
      toast.success(`Doctor account created — affiliations copied from admin`);
      setAddOpen(false);
      setDraft(EMPTY_NEW);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  const assignExisting = async () => {
    if (!assignDraft.email) { toast.error("Email is required"); return; }
    try {
      await api.post("/hospital/doctors/assign", { email: assignDraft.email, role: assignDraft.role });
      toast.success("Doctor assigned to this hospital");
      setAssignOpen(false);
      setAssignDraft(EMPTY_ASSIGN);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    try {
      const payload = {
        name: editing.name,
        role: editing.role,
        doctor_name: editing.doctor_name,
        specialty: editing.specialty,
        license_number: editing.license_number,
        ptr_number: editing.ptr_number,
      };
      await api.put(`/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditOpen(false);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const resetPassword = async () => {
    if (!newPw || newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/users/${pwTarget.id}/reset-password`, { password: newPw });
      toast.success(`Password reset for ${pwTarget.email}`);
      setPwOpen(false);
      setPwTarget(null);
      setNewPw("");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete account ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Account deleted");
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  if (user?.role !== "admin" && user?.role !== "hospital_admin") {
    return (
      <div className="p-8" data-testid="users-page">
        <Card className="p-6 border-amber-200 bg-amber-50 max-w-xl">
          <h2 className="font-semibold text-amber-900">Admin access required</h2>
          <p className="text-sm text-amber-800 mt-1">Only the clinic admin can manage doctor accounts. Sign in as an admin to continue.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Doctor Accounts</h1>
          <p className="text-slate-500 text-sm mt-1">Add another doctor — their affiliations + specialty + PTR will be auto-copied from your profile. They&apos;ll set their own name, license, and signature.</p>
        </div>
        <Button onClick={() => { setDraft(EMPTY_NEW); setAddOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-user">
          <UserPlus size={14} className="mr-2" /> Add Doctor
        </Button>
        <Button onClick={() => { setAssignDraft(EMPTY_ASSIGN); setAssignOpen(true); }} variant="outline" data-testid="btn-assign-doctor">
          <UserPlus size={14} className="mr-2" /> Assign Existing
        </Button>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Display Name</th>
              <th className="text-left px-4 py-3">Specialty</th>
              <th className="text-left px-4 py-3">License #</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-right px-4 py-3 w-48">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">No doctors yet.</td></tr>
            ) : items.map((u) => (
              <tr key={u.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{u.doctor_name || u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.specialty || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{u.license_number || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-[11px] uppercase tracking-wider ${
                    u.role === "admin" ? "bg-blue-100 text-blue-700"
                    : u.role === "doctor" ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-700"
                  }`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing({ ...u }); setEditOpen(true); }} data-testid={`btn-edit-${u.email}`}>
                    <Pencil size={13} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setPwTarget(u); setNewPw(""); setPwOpen(true); }} data-testid={`btn-pw-${u.email}`}>
                    <KeyRound size={13} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(u)} className="text-red-600" disabled={u.email === "doctor@rxprint.com" || u.id === user?.id} data-testid={`btn-del-${u.email}`}>
                    <Trash2 size={13} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle>Add Doctor Account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Email *</Label><Input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="cruz@rxprint.com" data-testid="new-user-email" /></div>
            <div><Label>Initial password *</Label><Input type="text" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} placeholder="At least 6 chars" data-testid="new-user-password" /></div>
            <div><Label>Account holder name *</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="DR. JOSE CRUZ" data-testid="new-user-name" /></div>
            <div><Label>Display Name on Rx (defaults to name)</Label><Input value={draft.doctor_name} onChange={(e) => setDraft({ ...draft, doctor_name: e.target.value })} placeholder="DR. JOSE CRUZ, MD" data-testid="new-user-doctor-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>License #</Label><Input value={draft.license_number} onChange={(e) => setDraft({ ...draft, license_number: e.target.value })} data-testid="new-user-license" /></div>
              <div><Label>PTR #</Label><Input value={draft.ptr_number} onChange={(e) => setDraft({ ...draft, ptr_number: e.target.value })} data-testid="new-user-ptr" /></div>
            </div>
            <div><Label>Specialty (defaults to admin&apos;s)</Label><Input value={draft.specialty} onChange={(e) => setDraft({ ...draft, specialty: e.target.value })} placeholder="Leave blank to inherit" data-testid="new-user-specialty" /></div>
            <div>
              <Label>Role</Label>
              <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="doctor">Doctor</SelectItem>
                  <SelectItem value="staff">Staff (no Rx signing)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
              Affiliations + signature can be edited by the new doctor in <b>My Profile</b> after they sign in. Your affiliations are copied as the starting template.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={create} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-create-user">Create Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit Account</DialogTitle></DialogHeader>
          {editing ? (
            <div className="space-y-3">
              <div><Label>Email (read-only)</Label><Input value={editing.email || ""} disabled /></div>
              <div><Label>Account name</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Display name on Rx</Label><Input value={editing.doctor_name || ""} onChange={(e) => setEditing({ ...editing, doctor_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>License #</Label><Input value={editing.license_number || ""} onChange={(e) => setEditing({ ...editing, license_number: e.target.value })} /></div>
                <div><Label>PTR #</Label><Input value={editing.ptr_number || ""} onChange={(e) => setEditing({ ...editing, ptr_number: e.target.value })} /></div>
              </div>
              <div><Label>Specialty</Label><Input value={editing.specialty || ""} onChange={(e) => setEditing({ ...editing, specialty: e.target.value })} /></div>
              <div>
                <Label>Workspace display name (optional)</Label>
                <Input value={editing.workspace_label || ""} onChange={(e) => setEditing({ ...editing, workspace_label: e.target.value })} placeholder="e.g. Gonzales Medical Clinic" data-testid="user-workspace-label" />
                <p className="text-[11px] text-slate-500 mt-1">Shown to THIS user instead of the clinic name — headers, menus, workspace switcher. Leave blank to use the clinic name.</p>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editing.role || "doctor"} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-user-edit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign existing doctor dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-md" data-testid="assign-doctor-dialog">
          <DialogHeader><DialogTitle>Assign Existing Doctor</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 mb-2">
            Assign a doctor (already a user in another hospital) to <b>your hospital</b>. They&apos;ll keep their RX profile.
          </p>
          <div className="space-y-3">
            <div><Label>Doctor email *</Label><Input value={assignDraft.email} onChange={(e) => setAssignDraft({ ...assignDraft, email: e.target.value })} placeholder="doctor@example.com" data-testid="assign-email" /></div>
            <div>
              <Label>Role at this hospital</Label>
              <Select value={assignDraft.role} onValueChange={(v) => setAssignDraft({ ...assignDraft, role: v })}>
                <SelectTrigger data-testid="assign-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="doctor">Doctor</SelectItem>
                  <SelectItem value="hospital_admin">Hospital Admin</SelectItem>
                  <SelectItem value="reception">Reception</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={assignExisting} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-assign-existing">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 mb-2">Set a new password for <b>{pwTarget?.email}</b>. Share it with them securely.</p>
          <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (≥6 chars)" data-testid="new-password-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button onClick={resetPassword} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-confirm-reset-pw">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
